import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { CASE_STUDY_NARRATIVES } from "../data/caseStudyNarratives";
// Batch 2 §4 — CASE_STUDY_IMAGE_BY_SLUG import removed. OG endpoint is no
// longer used as a hero fallback (that endpoint returns 1200×630 social
// share cards which get cropped mid-word when squeezed into a portrait
// hero slot). Hero source of truth is now the API `hero_image` field.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function setMeta(name, content, useProperty = false) {
  const attr = useProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Admin-uploaded portfolio photos scoped to this case study.
  const [serverPortfolio, setServerPortfolio] = useState([]);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    setCaseStudy(null);
    setNotFound(false);
    setServerPortfolio([]);
    axios.get(`${API}/case-studies/${slug}`)
      .then((r) => setCaseStudy(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      });
    axios
      .get(`${API}/event-photos`, { params: { target: "case-study", slug } })
      .then((r) => Array.isArray(r.data) && setServerPortfolio(r.data))
      .catch(() => setServerPortfolio([]));
  }, [slug]);

  // Update document title + OG meta tags + canonical whenever the case study changes
  useEffect(() => {
    if (!caseStudy) return;
    const prevTitle = document.title;
    const title = `${caseStudy.title} — JJ Argent`;
    const desc = caseStudy.summary || "";
    const ogImage = `${API}/og/case-studies/${caseStudy.slug}`;
    const canonicalUrl = `https://jjargent.com/case-studies/${caseStudy.slug}`;

    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, true);
    setMeta("og:description", desc, true);
    setMeta("og:image", ogImage, true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:type", "article", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", ogImage);

    // Canonical link
    let canonEl = document.querySelector('link[rel="canonical"]');
    const prevCanon = canonEl?.getAttribute("href") ?? null;
    if (!canonEl) {
      canonEl = document.createElement("link");
      canonEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonEl);
    }
    canonEl.setAttribute("href", canonicalUrl);

    // JSON-LD Article schema so recruiters get a rich card when they share the link.
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "jsonld-case-study";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: caseStudy.title,
      description: desc,
      image: ogImage,
      url: canonicalUrl,
      author: {
        "@type": "Person",
        name: "JJ Argent",
        url: "https://jjargent.com/",
      },
      publisher: {
        "@type": "Person",
        name: "JJ Argent",
      },
      about: caseStudy.org || undefined,
    });
    document.getElementById("jsonld-case-study")?.remove();
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (prevCanon !== null) canonEl?.setAttribute("href", prevCanon);
      document.getElementById("jsonld-case-study")?.remove();
    };
  }, [caseStudy]);

  if (notFound) return <Navigate to="/case-studies" replace />;

  if (!caseStudy) {
    return (
      <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6] min-h-screen flex items-center justify-center" data-testid="case-study-loading">
        <div className="w-8 h-8 border-2 border-[#D8A936] border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  const narrative = CASE_STUDY_NARRATIVES[slug];

  // Batch 2 §4 — API-driven hero and gallery when populated; fall back to the
  // legacy narratives file + OG endpoint so existing case studies keep working.
  const apiHero = caseStudy.hero_image;                    // { src, alt, caption?, credit? } | null
  const apiGallery = caseStudy.gallery || [];              // MediaAsset[]
  const apiChapters = caseStudy.chapters || [];            // CaseStudyChapter[]

  // Batch 2 §4 — hero source of truth is the API `hero_image` field. Fall
  // back to the legacy in-code narratives file for now (retained for
  // backward compatibility). The `/api/og/case-studies/*` endpoint is NEVER
  // used as a page hero — it's a 1200×630 social-share card that renders as
  // cropped text if pushed into a portrait slot (§4 root-cause fix).
  const heroImage = apiHero?.src || narrative?.hero.image || null;
  const heroAlt = apiHero?.alt || narrative?.hero.imageAlt || caseStudy.title;

  // Chapters: prefer API when it has content, otherwise fall back to the
  // narratives file. Same shape either way — the render code below is
  // schema-tolerant.
  const chapters = apiChapters.length > 0
    ? apiChapters.map((c) => ({
        n: c.n,
        title: c.title,
        body: c.body,
        image: c.image?.src || null,
        imageAlt: c.image?.alt || c.title,
        link: c.link_href ? { href: c.link_href, label: c.link_label || "Read more" } : null,
      }))
    : (narrative?.chapters || []);

  return (
    <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6]" data-testid={`case-study-detail-${slug}`}>
      {/* Reading-progress bar */}
      <motion.div
        style={{ scaleX: progress }}
        className="fixed top-[72px] left-0 right-0 h-[2px] bg-[#D8A936] origin-left z-40"
        aria-hidden
        data-testid="cs-progress"
      />

      {/* Back link */}
      <div className="pt-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <Link
          to="/case-studies"
          data-testid="cs-back"
          className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-display text-[#A9A9A6] hover:text-[#D8A936] transition-colors"
        >
          <ArrowLeft size={14} /> All case studies
        </Link>
      </div>

      {/* Hero */}
      <section className="pt-10 pb-16 lg:pb-24 relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="eyebrow">{narrative?.hero.eyebrow || caseStudy.tags?.join(" · ") || caseStudy.status}</p>
              <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.04em]">
                {narrative?.hero.headline || caseStudy.title}
                {narrative?.hero.accent && (
                  <>{" "}<br /><span className="text-[#D8A936]">{narrative.hero.accent}</span></>
                )}
              </h1>
              <p className="mt-8 text-lg leading-relaxed text-[#A9A9A6] max-w-2xl">
                {caseStudy.summary}
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {(caseStudy.tags || []).map((t) => (
                  <span key={t} className="text-[10px] tracking-[0.18em] uppercase text-[#D8A936] border border-[#D8A936]/40 px-2.5 py-1">
                    {t}
                  </span>
                ))}
              </div>
              <p className="mt-8 text-sm tracking-widest uppercase text-[#A9A9A6]">
                {caseStudy.org}
              </p>
            </div>

            {heroImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.16, 0.84, 0.24, 1] }}
                className="lg:col-span-5 border border-[#343437] overflow-hidden bg-[#151517] aspect-[4/5]"
                data-testid="cs-hero-image"
              >
                <img
                  src={heroImage}
                  alt={heroAlt}
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => {
                    // If the hero image fails, hide the entire panel so we
                    // never render alt text at display size.
                    const panel = e.currentTarget.closest("[data-testid='cs-hero-image']");
                    if (panel) panel.style.display = "none";
                  }}
                />
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Metrics band */}
      {narrative?.metrics && (
        <section className="border-y border-[#343437] bg-[#151517]" data-testid="cs-metrics">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
            {narrative.metrics.map((m, i) => (
              <motion.div
                key={m.label}
                initial={{ y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 0.84, 0.24, 1] }}
                data-testid={`cs-metric-${i}`}
              >
                <p className="oversize text-4xl md:text-5xl text-[#D8A936]">{m.value}</p>
                <p className="mt-3 text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">{m.label}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Chapters — scrollytelling. Data source: API `chapters` field when
          populated, else the legacy narratives file. */}
      {chapters.length > 0 && (
        <section className="relative" data-testid="cs-chapters">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 space-y-24 lg:space-y-32">
            {chapters.map((c, i) => {
              const flipped = i % 2 === 1;
              return (
                <motion.div
                  key={c.n}
                  initial={{ y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: [0.16, 0.84, 0.24, 1] }}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${flipped ? "lg:[&>*:first-child]:order-2" : ""}`}
                  data-testid={`cs-chapter-${c.n}`}
                >
                  <div className={`lg:col-span-5 ${c.image ? "" : "lg:col-span-12"}`}>
                    <div className="flex items-baseline gap-4 mb-6">
                      <span className="oversize text-5xl md:text-6xl text-[#D8A936]">{c.n}</span>
                      <span className="text-[10px] tracking-[0.24em] uppercase text-[#A9A9A6]">Chapter</span>
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight text-[#FAF9F6]">
                      {c.title}
                    </h2>
                    <p className="mt-6 text-[#A9A9A6] text-lg leading-relaxed">
                      {c.body}
                    </p>
                    {c.link && (
                      <Link
                        to={c.link.href}
                        data-testid={`cs-chapter-link-${c.n}`}
                        className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-display text-[#D8A936] border border-[#D8A936]/60 px-4 py-2.5 hover:bg-[#D8A936] hover:text-[#0B0B0C] transition-colors"
                      >
                        {c.link.label} <ArrowUpRight size={14} />
                      </Link>
                    )}
                  </div>
                  {c.image && (
                    <div className="lg:col-span-7">
                      <div className="border border-[#343437] overflow-hidden bg-[#151517] aspect-[16/10]">
                        <img
                          src={c.image}
                          alt={c.imageAlt || c.title}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* Portfolio strip — additional photos as a horizontal scroller.
          Merges three sources in priority order:
            1. serverPortfolio  → /api/event-photos?target=case-study (admin upload)
            2. apiGallery       → CaseStudy.gallery field (Batch 2 §4 schema)
            3. narrative.portfolio.items → legacy in-code fallback
          Dedup'd by src. Renders as long as any source has content. */}
      {(() => {
        const merged = [
          ...serverPortfolio.map((p) => ({
            src: p.url,
            caption: p.caption,
            meta: p.meta || p.event || "",
            alt: p.caption,
          })),
        ];
        const seen = new Set(merged.map((it) => it.src));
        // Batch 2 §4 — API gallery from the CaseStudy schema.
        apiGallery.forEach((it) => {
          if (it?.src && !seen.has(it.src)) {
            merged.push({ src: it.src, alt: it.alt, caption: it.caption || "", meta: it.credit || "" });
            seen.add(it.src);
          }
        });
        (narrative?.portfolio?.items || []).forEach((it) => {
          if (!seen.has(it.src)) merged.push(it);
        });
        if (merged.length === 0) return null;
        const eyebrow = narrative?.portfolio?.eyebrow || "Portfolio";
        const headline = narrative?.portfolio?.headline || "More from this project";
        const accent = narrative?.portfolio?.accent;
        const intro = narrative?.portfolio?.intro;
        return (
          <section
            data-testid="cs-portfolio-strip"
            className="border-t border-[#343437] bg-[#0B0B0C] py-20 lg:py-28"
          >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 items-end">
                <div className="lg:col-span-7">
                  <p className="eyebrow">{eyebrow}</p>
                  <h2 className="mt-5 font-display text-4xl md:text-5xl leading-[0.98] tracking-tight text-[#FAF9F6]">
                    {headline}
                    {accent && (
                      <>
                        {" "}<br />
                        <span className="text-[#D8A936]">{accent}</span>
                      </>
                    )}
                  </h2>
                </div>
                {intro && (
                  <div className="lg:col-span-5">
                    <p className="text-[#A9A9A6] leading-relaxed max-w-md">{intro}</p>
                  </div>
                )}
              </div>
              <div
                className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin"
                data-testid="cs-portfolio-scroller"
              >
                {merged.map((it, i) => (
                  <motion.figure
                    key={it.src}
                    initial={{ y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    data-testid={`cs-portfolio-item-${i}`}
                    className="snap-start flex-shrink-0 w-[85%] sm:w-[420px] border border-[#343437] bg-[#151517] overflow-hidden group"
                  >
                    <div className="aspect-[4/3] relative">
                      <img
                        src={it.src}
                        alt={it.alt || it.caption}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                      />
                    </div>
                    <figcaption className="px-4 py-3 border-t border-[#343437]">
                      <p className="text-sm text-[#FAF9F6] leading-tight">{it.caption}</p>
                      {it.meta && (
                        <p className="mt-1 text-[10px] tracking-[0.16em] uppercase text-[#D8A936]">{it.meta}</p>
                      )}
                    </figcaption>
                  </motion.figure>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Pull quote */}
      {narrative?.quote && (
        <section className="border-t border-[#343437] bg-[#0B0B0C] relative grain" data-testid="cs-quote">
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
            <motion.blockquote
              initial={{ y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <span aria-hidden className="block font-display text-[120px] leading-none text-[#D8A936]/25">"</span>
              <p className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#FAF9F6]">
                {narrative.quote.body}
              </p>
              <footer className="mt-10 font-signature text-3xl text-[#D8A936]">
                {narrative.quote.attribution}
              </footer>
            </motion.blockquote>
          </div>
        </section>
      )}

      {/* Fallback body when no narrative — still show the scale + tags */}
      {!narrative && (
        <section className="border-t border-[#343437] py-24" data-testid="cs-fallback">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <p className="eyebrow">Scale</p>
            <p className="mt-4 font-display text-3xl md:text-4xl text-[#FAF9F6] leading-tight max-w-3xl">
              {caseStudy.scale}
            </p>
            <p className="mt-8 text-[#A9A9A6] max-w-2xl leading-relaxed">
              A full scrollytelling write-up is on the way. In the meantime, reach out for the detailed briefing document —
              references, budgets, timelines and outcomes all available on request.
            </p>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="bg-[#0B0B0C] border-t border-[#343437]" data-testid="cs-closing">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow">Next step</p>
            <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#FAF9F6]">
              {narrative?.closing.cta || "Have a program you'd like to talk about?"}
            </h2>
            {narrative?.closing.lead && (
              <p className="mt-6 text-lg text-[#A9A9A6] max-w-2xl">{narrative.closing.lead}</p>
            )}
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="cs-cta-contact" className="btn-primary">
              Work with me <ArrowUpRight size={16} />
            </Link>
            <Link to="/case-studies" data-testid="cs-cta-all" className="btn-ghost">
              More case studies <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
